/* eslint-disable react-hooks/exhaustive-deps */
import { UploadStatus } from "@lyfegen-platform/react-common";
import { Box, Typography, useTheme } from "@material-ui/core";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useStyles from "../../containers/data/forms/forms-styles";
import { useSetFormBreadcrumbs } from "../../containers/data/forms/use-set-breadcrumbs";
import { GET_DISCONTINUATION_NOTE_ADMINISTRATIONS, SAVE_FLEX_ENTITIES, SAVE_FLEX_ENTITY } from "../../controllers/queries";
import { SaveFlexEntityMutation } from "../../controllers/types/queries.generated";
import { useDataMutation, useLazyDataQuery } from "../../hooks/useDataModule";
import { FlexFormPage } from "../../hooks/useFlexForm";
import { FlexContext } from "../../models/flexible/flex-context";
import { createSubmittedFormData } from "../../models/flexible/form-submission-data";
import { FlexContainerType, FlexFormStatus } from "../../models/generated/graphql";
import { flexibleFormResponseState, flexibleFormValuesState } from "../../store/flexible";
import { bulk } from "../../store/upload";
import useUrlQuery from "../../utils/use-url-query";
import DiscontinuationChecks from "../DiscontinuationChecks";
import FormStatus from "../mapped-components/new/FormStatus";
import FlexView from "./FlexView";
import { renderLoadingOrError } from "./utils";
import { ErrorImpactDialog, getErrorImpact } from "./FlexEntityErrorImpactDialog";

interface IProps {
	context: FlexContext;
	pages: Array<FlexFormPage>;
	entityId?: string;
	isRecordDrawer?: boolean;
	handleEditingModeTransition: (isStart: boolean) => void;
	dynamicParams?: { [key: string]: string };
	patients: Array<{ patientId: string; minAdministrationDate: string }>;
}

export function FlexWizard(props: IProps) {
	const { t } = useTranslation();
	const classes = useStyles();
	const { palette, spacing } = useTheme();
	const [page, setPage] = useState<number>(0);
	const [values, setValues] = useState<Array<any>>(props.pages.map((p) => p.initialValues));
	const query = useUrlQuery();
	const isNavigatedToEdit = query.has("startEdit");
	const setBulkDetails = useSetRecoilState(bulk);
	const { push, goBack } = useHistory();
	const [errorImpact, setErrorImpact] = useState<SaveFlexEntityMutation["SaveFlexEntity"][number] | null>(null);
	const [isCaseDeletionAllowed, setIsCaseDeletionAllowed] = useState<boolean>(false);
	const [mutate, { loading, error }] = useDataMutation<any>(
		props.dynamicParams?.multiplePatients ? SAVE_FLEX_ENTITIES : SAVE_FLEX_ENTITY,
		{
			onCompleted: (data) => {
				const errorImpact = getErrorImpact(data.SaveFlexEntity || data.SaveFlexEntities || []);

				if (!errorImpact || isCaseDeletionAllowed) {
					if (props.dynamicParams?.questId) {
						push("/data/records/quests");

						return;
					}

					const sanitizedRoute = props.context.entityType.containerType === FlexContainerType.Record ? "/records" : "";
					push(`/data${sanitizedRoute}`);
				}
				setErrorImpact(errorImpact);
			},
		}
	);
	const [mergedValuesState, setMergedValues] = useRecoilState(flexibleFormValuesState);
	const [areValuesMerged, setAreValuesMerged] = useState<boolean>(false);
	const initialData = useRecoilValue(flexibleFormResponseState);
	const [isNavigateOutAllowed, setIsNavigateOutAllowed] = useState<boolean>(false);
	const [discontinuationData, setDiscontinuationData] = useState<any>(null);
	const [load, { loading: discontinuationNoteLoading }] = useLazyDataQuery(GET_DISCONTINUATION_NOTE_ADMINISTRATIONS, {
		fetchPolicy: "no-cache",
		onCompleted: (data) => setDiscontinuationData(data),
	});

	useEffect(() => {
		setAreValuesMerged(false);
	}, [page]);

	useEffect(() => {
		const mergedValues = values.reduce((a, c) => ({ ...a, ...c }), {} as any);
		setMergedValues(mergedValues);
	}, []);

	useSetFormBreadcrumbs({
		formType: props.context.entityType.localizationKey,
		formId: props.entityId,
		isRecordDrawer: props.isRecordDrawer,
	});

	if (loading || !!error) {
		return renderLoadingOrError(loading, error)!;
	}

	function updatePage(pageValues: any) {
		const newValues = values.map((v, i) => (i === page ? pageValues : v));
		setValues(newValues);
		const mergedValues = newValues.reduce((a, c) => ({ ...a, ...c }), {} as any);
		setMergedValues(mergedValues);
		setAreValuesMerged(true);

		return mergedValues;
	}

	function handleNextPage(pageValues: any) {
		updatePage(pageValues);
		setPage(page + 1);
	}

	function handlePreviousPage(pageValues: any) {
		updatePage(pageValues);
		setPage(page - 1);
	}

	async function handleSave(pageValues: any, isSubmit: boolean, isConfirmed: boolean, isCaseDeletionAllowed: boolean = false) {
		const mergedValues = areValuesMerged ? mergedValuesState : updatePage(pageValues);
		const forms = props.dynamicParams?.multiplePatients
			? props.patients.map((patient) => ({
					...mergedValues,
					patientId: patient.patientId,
					coverageStartDate: patient.minAdministrationDate,
			  }))
			: [mergedValues];

		setIsNavigateOutAllowed(true);
		const isEditSubmittedForm = (props.context.flexEntity?.status as string) === FlexFormStatus.Submitted;

		if (props.dynamicParams?.multiplePatients) {
			setBulkDetails({
				isDisplayed: true,
				progress: 0,
				status: UploadStatus.GeneratingForms,
				type: FlexContainerType.Form,
			});
		}

		setIsCaseDeletionAllowed(isCaseDeletionAllowed);
		const formsDataToSubmit = forms.map((form) => ({
			...createSubmittedFormData(props.context, form, initialData),
		}));

		const hasDiscontinuationAttribute = props.dynamicParams?.multiplePatients ? false : !!formsDataToSubmit[0].discontinuationDate;

		if (
			isSubmit &&
			!isConfirmed &&
			!props.dynamicParams?.multiplePatients &&
			hasDiscontinuationAttribute &&
			!discontinuationData &&
			props.context.cases.length
		) {
			const discontinuationCheckResult = await load({
				variables: {
					caseId: props.context.cases[0].id,
					discontinuationDate: formsDataToSubmit[0].discontinuationDate,
				},
			});

			const hasRecordsToUnmatch =
				discontinuationCheckResult.data &&
				(discontinuationCheckResult.data.GetDiscontinuationNoteAdministrations.administrationsAfterDnWithDocument !== 0 ||
					discontinuationCheckResult.data.GetDiscontinuationNoteAdministrations.administrationsAfterDnWithoutDocument !== 0);

			if (hasRecordsToUnmatch) return;
		}

		const getStatus = () => {
			if (props.context.entityType.containerType === FlexContainerType.Record) {
				return FlexFormStatus.Unmatched;
			}

			return isSubmit || isEditSubmittedForm ? FlexFormStatus.Submitted : FlexFormStatus.Draft;
		};

		mutate({
			variables: {
				id: props.entityId || null,
				typeId: props.context.entityType.id,
				caseIds: props.context.cases.map((caseEntity) => caseEntity.id),
				data: props.dynamicParams?.multiplePatients
					? formsDataToSubmit.map((form) => JSON.stringify(form))
					: JSON.stringify(formsDataToSubmit[0]),
				status: getStatus(),
				isCaseDeletionAllowed,
			},
		});
	}

	function handleDiscard() {
		setErrorImpact(null);
		props.handleEditingModeTransition(false);
		if (isNavigatedToEdit) {
			goBack();
		}
	}

	return (
		<Fragment>
			{discontinuationData && (
				<DiscontinuationChecks
					values={values}
					handleSave={handleSave}
					error={error}
					discontinuationCheckData={discontinuationData}
				/>
			)}
			<Box display="flex" justifyContent="space-between" mb={3}>
				{props.context.entityType.containerType !== FlexContainerType.Record && (
					<Box display="flex" alignItems="center">
						<Typography variant="h5" className={classes.pageHeaderTextWithoutIndent} style={{ marginRight: spacing(3) }}>
							{t(props.context.entityType.localizationKey || "")} {props.entityId}
						</Typography>
						{
							<FormStatus
								children={<div />}
								palette={palette}
								t={t}
								props={{ status: props.context.flexEntity?.status || FlexFormStatus.Draft }}
							/>
						}
					</Box>
				)}
				{props.pages.length > 1 && (
					<Typography variant="h5" className={classes.pageHeaderText}>
						{`${t("page")} ${page + 1} ${t("of")} ${props.pages.length}`}
					</Typography>
				)}
			</Box>
			<FlexView
				key={page}
				context={props.pages[page].subtypeAggregate}
				validationSchema={props.pages[page].validationSchema}
				initialValues={values[page]}
				canSelectNextPage={page < props.pages.length - 1}
				canSelectPreviousPage={page > 0}
				handleNextPage={handleNextPage}
				handlePreviousPage={handlePreviousPage}
				handleEditingModeTransition={props.handleEditingModeTransition}
				handleSave={(values) => handleSave(values, false, false)}
				handleSubmit={(values) => handleSave(values, true, false)}
				setIsNavigateOutAllowed={setIsNavigateOutAllowed}
				isRecordDrawer={props.isRecordDrawer}
				patients={props.patients}
				loading={discontinuationNoteLoading}
			/>
			<Prompt
				when={props.pages[page].subtypeAggregate.isEditable && !isNavigateOutAllowed && !errorImpact}
				message={t("changes-made-not-saved")}
			/>
			<ErrorImpactDialog
				errorImpact={errorImpact}
				isCaseDeletionAllowed={isCaseDeletionAllowed}
				onAllowCaseDeletion={() => {
					setIsCaseDeletionAllowed(true);
					handleSave(values[page], true, true, true);
				}}
				onDiscardChange={handleDiscard}
				onClose={() => setErrorImpact(null)}
			/>
		</Fragment>
	);
}
